import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, Coins, Crown, ChevronRight } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Session manquante" });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled || attempts >= 8) {
        if (!cancelled) setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/stripe/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          return;
        }
        if (data.status === "expired" || (data.payment_status === "unpaid" && data.status === "complete")) {
          setState({ status: "failed", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-5 md:px-10 py-16 md:py-24 text-center" data-testid="billing-success">
      {state.status === "loading" && (
        <>
          <Loader2 className="w-12 h-12 animate-spin text-[#E5276A] mx-auto" />
          <h1 className="font-display text-3xl text-[#1F3A5F] mt-6">Vérification du paiement…</h1>
          <p className="text-sm text-[#4A5568] mt-2">Cela prend quelques secondes. Ne fermez pas cette page.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" data-testid="billing-success-icon" />
          <h1 className="font-display text-4xl text-[#1F3A5F] mt-6">Paiement réussi 🍷</h1>
          <p className="text-base text-[#4A5568] mt-3">{state.data?.package_label}</p>
          {state.data?.amount_total != null && (
            <p className="text-2xl font-display text-[#E5276A] mt-2">
              {(state.data.amount_total / 100).toFixed(2)} {state.data.currency?.toUpperCase()}
            </p>
          )}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FCE4ED] text-[#E5276A] font-bold">
            {state.data?.plan_granted ? <Crown className="w-4 h-4" /> : <Coins className="w-4 h-4" />}
            {state.data?.plan_granted ? "Plan activé" : "Crédits ajoutés"}
          </div>
          <div className="mt-8 space-y-2">
            <Link to="/guide" className="block px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] inline-flex items-center justify-center gap-2" data-testid="billing-success-cta">
              Découvrir tout ce que vous pouvez faire <ChevronRight className="w-4 h-4" />
            </Link>
            <Link to="/" className="text-xs text-[#8B95A1] hover:text-[#1F3A5F] inline-block mt-2">Retour à l'accueil</Link>
          </div>
        </>
      )}
      {(state.status === "failed" || state.status === "error") && (
        <>
          <XCircle className="w-14 h-14 text-red-500 mx-auto" />
          <h1 className="font-display text-3xl text-[#1F3A5F] mt-6">Paiement non finalisé</h1>
          <p className="text-sm text-[#4A5568] mt-2">{state.message || "Aucun débit n'a été effectué."}</p>
          <button onClick={() => navigate("/pricing")} className="mt-6 px-6 py-3 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661]">
            Réessayer
          </button>
        </>
      )}
      {state.status === "timeout" && (
        <>
          <Loader2 className="w-12 h-12 text-amber-500 mx-auto" />
          <h1 className="font-display text-3xl text-[#1F3A5F] mt-6">Vérification en cours</h1>
          <p className="text-sm text-[#4A5568] mt-2">Le paiement est en cours de traitement. Vous recevrez un email de confirmation et vos crédits seront crédités sous peu.</p>
          <Link to="/" className="mt-6 inline-block px-6 py-3 rounded-xl bg-[#1F3A5F] text-white font-bold">Retour à l'accueil</Link>
        </>
      )}
    </div>
  );
}
